'use client';

import React, { useContext, useEffect, useMemo, useState } from 'react';

import { FundingKey, IFunding } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import {
    Align,
    Box,
    BoxContent,
    BoxHeader,
    Button,
    ButtonVariant,
    DirectionEnum,
    FlexBox,
    FlexItem,
    GapSize,
    Icon,
    IconName,
    IconSize,
    Justify,
    Popover,
    PopoverDirection,
    Price,
    PriceLevel,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TypographyAlign,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { DiscountType } from '@app-types/discount';
import { MobilePlanType } from '@app-types/plan';
import { UserType, UserTypeEnum } from '@app-types/user';

import { InlinePrice } from '@components/inline-price';
import { LockedPlanSwitch } from '@components/product-details-modal/locked-plan-switch';

import { getPricesWithPromos } from '@helpers/offer-wall';
import { LockedPlanValue, getBlockedPlans, setPlanLock } from '@helpers/plan-lock';
import { formatPrice } from '@helpers/wording';

import { MarketingType, PlanRange, PlanTypeId } from '@constants/plan';

import { BigInfoBox } from './big-info-box';
import { OfferCardPepites } from './offer-card-pepites';

type OfferCardProps = {
    user?: UserType;
    offer: MobilePlanType;
    openOfferDetails: (offer: MobilePlanType) => void;
    isPhoneLoading: boolean;
    onAddToCart: (selectedOffer: MobilePlanType, fundingKey?: FundingKey) => Promise<void>;
    isConnected: boolean;
    isSmallPrice: boolean;
    showBlockedPlanZone: boolean;
    hatInLine: boolean;
    selectedFunding?: IFunding;
    customCtaLabel?: string;
    customName?: string;
    hasBigInnerHeight?: boolean;
};

export function OfferCard({
    user,
    offer,
    openOfferDetails,
    isPhoneLoading,
    onAddToCart,
    isConnected,
    isSmallPrice,
    showBlockedPlanZone = false,
    hatInLine,
    selectedFunding,
    customName,
    hasBigInnerHeight,
}: Readonly<OfferCardProps>) {
    const { setFundingModeMutation } = useContext(CartContext);
    const productDetails = offer.details;
    const [isLoading, setIsLoading] = useState(false);
    const [isLocked, setIsLocked] = useState<boolean>(false);

    const offerPrices = useMemo(
        () => getPricesWithPromos(productDetails),
        [productDetails?.price?.forever, productDetails?.discounts],
    );

    const discounts = productDetails?.discounts;

    const { homeDiscount, multilineDiscount } = useMemo(() => {
        let home: DiscountType | undefined;
        let multiline: DiscountType | undefined;

        for (const d of discounts ?? []) {
            if (d.marketingTypes?.includes(MarketingType.HOME) && !d.isIncitation && !home) {
                home = d;
            }
            if (d.marketingTypes?.includes(MarketingType.MULTILINE) && !multiline) {
                multiline = d;
            }
            if (home && multiline) break;
        }

        return { homeDiscount: home, multilineDiscount: multiline };
    }, [discounts]);

    const shouldShowBigInfoBox = isSmallPrice ? multilineDiscount : homeDiscount;

    useEffect(() => {
        if (offer.entityId) {
            setIsLocked(getBlockedPlans()[offer.entityId] || false);
        }
    }, [offer.entityId]);

    if (!offerPrices) {
        return null;
    }

    const handleAddToCart = async () => {
        setIsLoading(true);
        if (selectedFunding) {
            await setFundingModeMutation.mutateAsync({
                fundingKey: selectedFunding.key,
                callbackUrl: `${window.location.origin}/tunnel/redirect`, // Unused in virtual cart
            });
        }
        await onAddToCart(offer, selectedFunding?.key);
        setIsLoading(false);
    };

    const shouldShowAddToCartButton = !(
        user?.user_type === UserTypeEnum.CDC &&
        offer.isOwned &&
        (offer.typeId.includes(PlanTypeId.SOWO) || offer.commercialRange === PlanRange.WITHOUT_MOBILE)
    );

    const smallDiscountIncitationFalse = multilineDiscount?.isIncitation ? undefined : multilineDiscount;
    const eligibleDiscount = isSmallPrice ? smallDiscountIncitationFalse : homeDiscount;
    const loggedInDiscount = isConnected ? eligibleDiscount : undefined;

    const getBigInfoBoxLabel = () => {
        if (isSmallPrice) {
            return `${loggedInDiscount ? 'Remise' : 'Prix déjà'} client Bbox + forfait`;
        }
        return `${loggedInDiscount ? 'Remise' : 'Prix'} client Bbox`;
    };
    const bigInfoBoxLabel = getBigInfoBoxLabel();

    return (
        <Box
            data-cy={`plan-${offer.gencode}`}
            fullheight
            headerOffset={!(offer?.planFlag || offer.isOwned || !hatInLine)}
            shadowless
        >
            {(offer?.planFlag || offer.isOwned) && (
                <BoxHeader className='is-justified-center'>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: (offer.isOwned ? 'Mon forfait actuel' : offer?.planFlag)!,
                        }}
                        className='is-flex is-aligned-center'
                    />
                </BoxHeader>
            )}
            <BoxContent>
                <FlexBox
                    direction={DirectionEnum.COLUMN}
                    justify={Justify.SPACE_BETWEEN}
                    fullheight
                    gap={{
                        mobile: GapSize.ONE,
                    }}
                >
                    <FlexItem>
                        <FlexBox
                            direction={DirectionEnum.COLUMN}
                            gap={{
                                mobile: GapSize.THREE,
                                desktop: GapSize.FIVE,
                            }}
                        >
                            <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                                <FlexBox
                                    direction={DirectionEnum.COLUMN}
                                    gap={GapSize.SEVEN}
                                    className={hasBigInnerHeight ? 'big-info-box' : undefined}
                                >
                                    <FlexItem narrow>
                                        <FlexBox justify={Justify.SPACE_BETWEEN}>
                                            <div>
                                                <Title markup='h2' level={TitleLevels.THREE} marginless>
                                                    {customName ?? offer.sexyName}
                                                </Title>

                                                <Title level={TitleLevels.FIVE} marginless>
                                                    {offer.dataEnvelope}
                                                </Title>
                                            </div>
                                            <Icon
                                                name={offer?.maxConnections === '5g' ? IconName.UI_5G : IconName.UI_4G}
                                                size='medium'
                                            />
                                        </FlexBox>
                                    </FlexItem>
                                    <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                                        <FlexBox
                                            direction={DirectionEnum.COLUMN}
                                            gap={GapSize.TWO}
                                            className={`main-price-bloc ${offerPrices?.duration ? 'with-y1y2-price' : ''}`}
                                        >
                                            <FlexItem narrow>
                                                <View justify={Justify.CENTER} align={Align.CENTER} flexable>
                                                    <Price
                                                        amount={
                                                            loggedInDiscount ? offerPrices.final : offerPrices.initial
                                                        }
                                                        period='mois'
                                                        level={PriceLevel.FOUR}
                                                        data-cy='plan-final-price'
                                                    />
                                                </View>
                                            </FlexItem>

                                            <FlexItem narrow>
                                                <FlexBox
                                                    justify={Justify.CENTER}
                                                    gap={GapSize.TWO}
                                                    direction={DirectionEnum.ROW}
                                                >
                                                    <FlexItem narrow>
                                                        {(offerPrices?.duration ?? 0) > 0 && (
                                                            <Text
                                                                typo={[
                                                                    TypographyAlign.TEXT_CENTERED,
                                                                    TypographyBold.TEXT_WEIGHT_SEMIBOLD,
                                                                ]}
                                                                marginless
                                                                level={TextLevels.THREE}
                                                            >
                                                                puis&nbsp;
                                                                <InlinePrice
                                                                    level={TextLevels.THREE}
                                                                    amount={
                                                                        isConnected
                                                                            ? offerPrices.forever
                                                                            : offerPrices.foreverWithoutBig
                                                                    }
                                                                    period='mois'
                                                                />
                                                                &nbsp;après {offerPrices.duration} mois
                                                            </Text>
                                                        )}
                                                        <Text
                                                            marginless
                                                            typo={TypographyAlign.TEXT_CENTERED}
                                                            level={TextLevels.THREE}
                                                            className='capitalize-first'
                                                        >
                                                            {offer.obligationLabel}
                                                        </Text>
                                                    </FlexItem>

                                                    <FlexItem narrow>
                                                        {(offerPrices.duration || 0) > 0 &&
                                                            offerPrices.foreverWithoutBig &&
                                                            offerPrices.final !== offerPrices.forever && (
                                                                <Popover
                                                                    direction={PopoverDirection.LEFT}
                                                                    trigger={
                                                                        <Text level={TextLevels.THREE} marginless>
                                                                            <Icon
                                                                                size={IconSize.SMALLER}
                                                                                name={IconName.INFOS_CIRCLE}
                                                                            />
                                                                        </Text>
                                                                    }
                                                                >
                                                                    Prix sans conservation du numéro&nbsp;:{' '}
                                                                    {isConnected
                                                                        ? formatPrice(offerPrices.forever)
                                                                        : formatPrice(offerPrices.foreverWithoutBig)}
                                                                    /mois
                                                                </Popover>
                                                            )}
                                                    </FlexItem>
                                                </FlexBox>
                                            </FlexItem>
                                        </FlexBox>
                                        {shouldShowBigInfoBox && (
                                            <BigInfoBox
                                                offer={offer}
                                                offerPrices={offerPrices}
                                                label={bigInfoBoxLabel}
                                                loggedInDiscount={loggedInDiscount}
                                                popover={
                                                    isSmallPrice && !loggedInDiscount ? (
                                                        <Popover
                                                            trigger={
                                                                <Text level={TextLevels.THREE} marginless>
                                                                    <Icon
                                                                        size={IconSize.SMALLER}
                                                                        name={IconName.INFOS_CIRCLE}
                                                                    />
                                                                </Text>
                                                            }
                                                        >
                                                            Pour tout client détenteur dune offre Bbox et d’un forfait
                                                            Bouygues Telecom 20 Go et + (hors B&YOU)
                                                        </Popover>
                                                    ) : null
                                                }
                                            />
                                        )}
                                    </FlexBox>
                                </FlexBox>
                                {showBlockedPlanZone && (
                                    <div className='blocked-plan-switch'>
                                        <LockedPlanSwitch
                                            id={offer.entityId!}
                                            offer={offer}
                                            checked={isLocked}
                                            onChange={() => {
                                                if (offer.locked === LockedPlanValue.LOCK_FORCED) {
                                                    return;
                                                }
                                                setIsLocked(!isLocked);
                                                setPlanLock(offer.entityId!, !isLocked);
                                            }}
                                        />
                                    </div>
                                )}
                            </FlexBox>
                            {shouldShowAddToCartButton && (
                                <Button
                                    loading={isLoading || isPhoneLoading}
                                    onClick={handleAddToCart}
                                    type='button'
                                    variant={ButtonVariant.CONVERSION}
                                    fullwidth
                                    data-cy='cta-show-plan-upgrade-modal'
                                >
                                    Continuer avec ce forfait
                                </Button>
                            )}

                            <OfferCardPepites offer={offer} />
                        </FlexBox>
                    </FlexItem>

                    <FlexItem>
                        <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE} justify={Justify.END} fullheight>
                            <Button
                                onClick={() => openOfferDetails(offer)}
                                type='button'
                                variant={ButtonVariant.GHOST}
                                fullwidth
                                accessibilityLabel={`${offer.name}, voir les détails`}
                            >
                                Voir les détails
                            </Button>
                            <Text level={TextLevels.THREE} marginless>
                                *&nbsp;Liste des destinations dans &quot;Voir les détails&quot;
                            </Text>
                        </FlexBox>
                    </FlexItem>
                </FlexBox>
            </BoxContent>
        </Box>
    );
}
